import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle } from "lucide-react";
import { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { API_ENDPOINTS } from "../../services/apiEndpoints";
import { getVirtualCourses } from "../../services/dashboardService";
import { api1 } from "../../utils/axiosInstance";
import StatusModal from "../StatusModal/StatusModal";
import { DateTime } from 'luxon';
import CourseCard from "../onTrackCourses/CourseCard";

const VirtualCourses = ({ type, filters = {} }) => {
  const stripe = useStripe();
  const elements = useElements();
  const userId = useSelector((state) => state.auth.id);
  const queryClient = useQueryClient();
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [statusModal, setStatusModal] = useState({
    open: false,
    type: "",
    message: "",
  });

  const {
    data: fetchVirtualCourses,
    isLoading: fetchVirtualCoursesPending,
    error: fetchVirtualCoursesError,
  } = useQuery({
    queryKey: ["getVirtualCourses", userId],
    queryFn: getVirtualCourses,
    enabled: !!userId,
  });

  const formatCourseData = (courseList) => {
    if (!courseList) return [];
    return courseList.map((course) => {
      const dateInTimeZone = DateTime.fromISO(course.date)
        .setZone(course.timezone);

      return {
        id: course._id,
        name: course.courseName,
        description: course.courseDescription,
        instructor: course.instructorName,
        date: dateInTimeZone.toFormat("MMMM d, yyyy"),
        time: dateInTimeZone.toFormat("hh:mm a"),
        timezone: course.timezone,
        price: course.price,
        seatsAvailable: course.seats,
        duration: course.duration,
        registeredUsers: course.registeredUsers,
        institutionName: course.institutionName,
        // Add fields for new UI
        title: course.courseName,
        rating: 4.8, // Default rating - you might want to get this from API
        difficulty: course.difficultyLevel || "intermediate", // Get from course data
        difficultyLevel: course.difficultyLevel || "intermediate",
        isFree: course.price === 0
      };
    });
  };

  const handleClick = async (course) => {
    setSelectedCourse(course);
  };

  const handlePaymentSubmit = async () => {
    if (!selectedCourse || !stripe || !elements) return;

    setPaymentProcessing(true);
    try {
      const { data: clientSecret } = await api1.post(
        "/api/stripe/create-payment-intent",
        {
          amount: selectedCourse.price * 100,
          currency: "usd",
          metadata: {
            courseId: selectedCourse.id,
            userId: userId,
          },
        }
      );

      const { error, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret.clientSecret,
        {
          payment_method: {
            card: elements.getElement(CardElement),
          },
        }
      );

      if (error) {
        throw new Error(error.message);
      }

      if (paymentIntent.status === "succeeded") {
        await api1.post(
          `${API_ENDPOINTS.REGISTER_COURSE}/${selectedCourse.id}`,
          { userId }
        );

        await queryClient.invalidateQueries(["getVirtualCourses", userId]);
        setStatusModal({
          open: true,
          type: "success",
          message: "Payment successful! You are now registered for the course.",
        });
      }
    } catch (error) {
      console.error("Error during payment process:", error);
      setStatusModal({
        open: true,
        type: "error",
        message: `Payment failed: ${
          error.message || "An unexpected error occurred."
        }`,
      });
    } finally {
      setSelectedCourse(null);
      setPaymentProcessing(false);
    }
  };

  // 🔍 Filter by courseType and additional filters (memoized for performance)
  const filteredCourses = useMemo(() => {
    if (!fetchVirtualCourses || !Array.isArray(fetchVirtualCourses)) {
      return [];
    }

    // First filter by courseType if type is passed
    let filtered = type
      ? fetchVirtualCourses.filter((course) => course.courseType === type)
      : fetchVirtualCourses;

    // Check if any filters are actually selected (not just empty arrays)
    const hasActiveFilters = filters && (
      (filters.difficulty && filters.difficulty.length > 0) ||
      (filters.duration && filters.duration.length > 0) ||
      (filters.price && filters.price.length > 0)
    );

    // Apply additional filters (difficulty, duration, price)
    if (hasActiveFilters && filtered.length > 0) {
      filtered = filtered.filter((course) => {
        // Difficulty filter
        if (filters.difficulty && filters.difficulty.length > 0) {
          const courseDifficulty = course.difficultyLevel?.toLowerCase();
          if (!filters.difficulty.includes(courseDifficulty)) {
            return false;
          }
        }

        // Duration filter (in weeks)
        if (filters.duration && filters.duration.length > 0) {
          const courseDuration = course.duration || 0;
          const matchesDuration = filters.duration.some((range) => {
            if (range === "1-4") return courseDuration >= 1 && courseDuration <= 4;
            if (range === "5-8") return courseDuration >= 5 && courseDuration <= 8;
            if (range === "9-12") return courseDuration >= 9 && courseDuration <= 12;
            if (range === "13+") return courseDuration >= 13;
            return false;
          });
          if (!matchesDuration) {
            return false;
          }
        }

        // Price filter
        if (filters.price && filters.price.length > 0) {
          const coursePrice = course.price || 0;
          const matchesPrice = filters.price.some((range) => {
            if (range === "free") return coursePrice === 0;
            if (range === "under50") return coursePrice > 0 && coursePrice < 50;
            if (range === "50-200") return coursePrice >= 50 && coursePrice <= 200;
            if (range === "200+") return coursePrice > 200;
            return false;
          });
          if (!matchesPrice) {
            return false;
          }
        }

        return true;
      });
    }

    return filtered;
  }, [fetchVirtualCourses, type, filters]);

  const courses = useMemo(() => formatCourseData(filteredCourses), [filteredCourses]);

  return (
    <>
      <StatusModal
        isOpen={statusModal.open}
        onClose={() => setStatusModal({ ...statusModal, open: false })}
        type={statusModal.type}
        message={statusModal.message}
      />

      {fetchVirtualCoursesPending ? (
        <div className="flex justify-center items-center p-6">
          <p>Loading courses...</p>
        </div>
      ) : fetchVirtualCoursesError ? (
        <div className="flex flex-col items-center justify-center p-6 text-red-500">
          <AlertCircle className="w-8 h-8 mb-2" />
          <p>Error loading courses. Please try again later.</p>
        </div>
      ) : courses.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-6 text-gray-500">
          <AlertCircle className="w-8 h-8 mb-2" />
          <p>No courses available at the moment. Please check back later.</p>
        </div>
      ) : (
        <>
          {/* Course Grid with new UI */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {courses.map((course) => (
              <CourseCard key={course.id} course={course} onClick={() => handleClick(course)} />
            ))}
          </div>

          {/* Payment Modal */}
          {selectedCourse && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
              <div className="bg-white rounded-lg p-6 max-w-md w-full">
                <h3 className="text-lg font-bold mb-4">
                  Complete Payment for {selectedCourse.name}
                </h3>
                <p className="mb-4">
                  Amount: ${selectedCourse.price.toFixed(2)}
                </p>

                <div className="mb-4 p-4 border rounded">
                  <CardElement options={{ hidePostalCode: true }} />
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setSelectedCourse(null)}
                    className="px-4 py-2 border rounded"
                    disabled={paymentProcessing}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handlePaymentSubmit}
                    className="px-4 py-2 bg-blue-500 text-white rounded"
                    disabled={paymentProcessing || !stripe}
                  >
                    {paymentProcessing ? "Processing..." : "Pay Now"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default VirtualCourses;
